import React from 'react';
import { UploadCloudIcon, ImageIcon } from 'lucide-react';
import { useProductManager } from './product-manager/useProductManager';
import { TypeSelectionScreen } from './product-manager/TypeSelectionScreen';
import { ConfirmationScreen } from './product-manager/ConfirmationScreen';
import { PublishConfirmModal } from './product-manager/PublishConfirmModal';
import { EditionModal } from './product-manager/EditionModal';
import { FormHeader } from './product-manager/FormHeader';
import { ModeToggleSelector } from './product-manager/ModeToggleSelector';
import { BasicInfoSection } from './product-manager/BasicInfoSection';
import { CategoriesTagsSection } from './product-manager/CategoriesTagsSection';
import { GenreDetailsSection } from './product-manager/GenreDetailsSection';
import { BookBoxDetailsSection } from './product-manager/BookBoxDetailsSection';
import { AvailabilityServicesSection } from './product-manager/AvailabilityServicesSection';
import { CollaboratorsSection } from './product-manager/CollaboratorsSection';
import { MediaUploadSection } from './product-manager/MediaUploadSection';
import { PricingSection } from './product-manager/PricingSection';
import { EditionsSection } from './product-manager/EditionsSection';
import { LinkedEditionsSection } from './product-manager/LinkedEditionsSection';
import { ExternalLinksSection } from './product-manager/ExternalLinksSection';
import { SaleFormSection } from './product-manager/SaleFormSection';
import { StoryDetailsSection } from './product-manager/StoryDetailsSection';
import { CoverImagesSection } from './product-manager/CoverImagesSection';
import { useDarkClasses } from '../../hooks/useDarkClasses';
// Event Manager sections
import { useEventManager } from './event-manager/useEventManager';
import { EventBasicsSection } from './event-manager/EventBasicsSection';
import { DateTimeSection } from './event-manager/DateTimeSection';
import { LocationSection } from './event-manager/LocationSection';
import { AccessRulesSection } from './event-manager/AccessRulesSection';
import { CapacitySection } from './event-manager/CapacitySection';
import { VisibilitySection } from './event-manager/VisibilitySection';
const ProductManager = () => {
  const pm = useProductManager();
  const em = useEventManager();
  const dk = useDarkClasses();
  // Type Selection Screen
  if (!pm.productType) {
    return <TypeSelectionScreen darkMode={pm.darkMode} onTypeSelect={pm.handleTypeSelect} data-id="element-21316" data-mp="3a401qc3lke4j" />;
  }
  // Determine if we're in event mode
  const isEvent = pm.productType === 'event';
  // For events, use the event manager's confirmation state
  const showConfirmation = isEvent ? em.showConfirmation : pm.showConfirmation;
  return <div className={`min-h-screen pt-20 md:pt-6 pb-6 sm:pt-20 sm:pb-8 md:pt-8 ${dk.page} ${dk.textPrimary}`} data-id="element-21317" data-mp="2rc7aam46f0yx">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21318" data-mp="2mb9u2f2q8h02">
        {/* Header */}
        {isEvent ? <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-21319" data-mp="2hf1krib7ni0t">
            <div className="flex items-center gap-4" data-id="element-21320" data-mp="27b4cbbpr96ws">
              <button onClick={() => pm.setProductType(null)} className={`p-2 rounded-lg border transition-colors ${dk.surface} ${dk.border} ${dk.textPrimary} hover:bg-gray-100 dark:hover:bg-gray-800`} data-id="element-21321" data-mp="2c7bjt8xsuqgu">
                ←
              </button>
              <div data-id="element-21322" data-mp="1oj20qerz0gte">
                <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-21323" data-mp="1ojv21085ue45">
                  {em.mode === 'create' ? 'Create New Event' : 'Edit Event'}
                </h1>
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-21324" data-mp="1yg6gnwv83g1r">
                  Submit structured event details for readers and vendors.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-21325" data-mp="15sakct9kfq5i">
              <button onClick={em.handleSaveDraft} disabled={em.isProcessing} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${em.darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-21326" data-mp="15rsgpbkkq9nk">
                Save Draft
              </button>
              <button onClick={em.handlePublish} disabled={em.isProcessing} className={`px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors ${em.isProcessing ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`} data-id="element-21327" data-mp="3m7p59ek8qit1">
                {em.isProcessing ? 'Publishing...' : 'Publish Event'}
              </button>
            </div>
          </div> : <FormHeader pm={pm} data-id="element-21328" data-mp="3zzopj49j754" />}

        {showConfirmation ? isEvent ? <ConfirmationScreen pm={{
        productType: 'event',
        mode: em.mode,
        savedAsDraft: em.savedAsDraft,
        darkMode: em.darkMode,
        navigate: em.navigate,
        handleTypeSelect: () => pm.setProductType(null)
      } as any} data-id="element-21329" data-mp="2aqph8v4xeq4t" /> : <ConfirmationScreen pm={pm} data-id="element-21330" data-mp="bksf55691kx0" /> : <>
            {/* Mode Toggle & Item Selector (not for events - events use their own flow) */}
            {!isEvent && <ModeToggleSelector pm={pm} data-id="element-21331" data-mp="2jly8uzou838" />}

            {/* Form Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-21332" data-mp="2louhwrfoxge">
              {/* Left Column - Main Info */}
              <div className="lg:col-span-2 space-y-6" data-id="element-21333" data-mp="3oqsgr9vvvyrk">
                {isEvent ? <>
                    <EventBasicsSection em={em} data-id="element-21334" data-mp="3tp3sxr9nilcy" />
                    <DateTimeSection em={em} data-id="element-21335" data-mp="3tpvnqzvfcf26" />
                    <LocationSection em={em} data-id="element-21336" data-mp="1mynvuicsy1qc" />
                    <AccessRulesSection em={em} data-id="element-21337" data-mp="1mzywwaev4vdr" />
                  </> : pm.productType === 'sale' ? <SaleFormSection pm={pm} data-id="element-21338" data-mp="1rvwfkndlmdtp" /> : pm.productType === 'book' ? <>
                    <BasicInfoSection pm={pm} data-id="element-21339" data-mp="1lia1vk90qkjp" />
                    <GenreDetailsSection pm={pm} data-id="element-21340" data-mp="1lgff00e6sicz" />
                    <CollaboratorsSection pm={pm} data-id="element-21341" data-mp="1ginnh7hgey9b" />
                  </> : <>
                    <BasicInfoSection pm={pm} data-id="element-21342" data-mp="1ci3r464pfyo8" />
                    <CategoriesTagsSection pm={pm} data-id="element-21343" data-mp="1cjeuw5y5h6fc" />

                    {/* Book Box Specifics */}
                    {pm.productType === 'bookbox' && <BookBoxDetailsSection pm={pm} data-id="element-21344" data-mp="2488aq2kn4hoe" />}

                    {/* Creative/Service Specifics */}
                    {(pm.productType === 'creative' || pm.productType === 'service') && <AvailabilityServicesSection pm={pm} data-id="element-21345" data-mp="1ve3ltop2hgbi" />}

                    {/* Collaborators */}
                    {pm.productType !== 'creative' && pm.productType !== 'service' && <CollaboratorsSection pm={pm} data-id="element-21346" data-mp="9p9bargktb68" />}
                  </>}
              </div>

              {/* Right Column - Media & Pricing */}
              <div className="space-y-6" data-id="element-21347" data-mp="ipnzbykxd4df">
                {isEvent ? <>
                    <CapacitySection em={em} data-id="element-21348" data-mp="no9327zdy66m" />
                    <VisibilitySection em={em} data-id="element-21349" data-mp="nq2avqlqiwg7" />

                    {/* Event Media Upload */}
                    <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-21350" data-mp="smsmo1qib5vt">
                      <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-21351" data-mp="xlvj4xsf83s6">
                        <ImageIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-21352" data-mp="12fhy4wm24jr5" />
                        Event Images
                      </h2>
                      <div className="space-y-4" data-id="element-21353" data-mp="1belh80l84z5n">
                        <div data-id="element-21354" data-mp="1bfwj1uv3zwu4">
                          <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21355" data-mp="1gcwtehyzrlr1">
                            Cover Image
                          </label>
                          <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${em.darkMode ? 'border-gray-600 hover:border-purple-500 bg-gray-800/50' : 'border-gray-300 hover:border-purple-500 bg-gray-50'}`} data-id="element-21356" data-mp="m9v04lz5k03k">
                            <UploadCloudIcon className={`w-8 h-8 mx-auto mb-2 ${dk.textFaint}`} data-id="element-21357" data-mp="8kb562btc74t" />
                            <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-21358" data-mp="hbl4ybdfgzhy">
                              Click to upload
                            </p>
                            <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-21359" data-mp="3l04vtpm7n7n4">
                              Square or 4:3 recommended
                            </p>
                          </div>
                        </div>
                        <div data-id="element-21360" data-mp="36umk8pe0vwm3">
                          <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21361" data-mp="36tjrebx53k3j">
                            Banner Image (Optional)
                          </label>
                          <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${em.darkMode ? 'border-gray-600 hover:border-purple-500 bg-gray-800/50' : 'border-gray-300 hover:border-purple-500 bg-gray-50'}`} data-id="element-21362" data-mp="31vi9hubg9qtn">
                            <UploadCloudIcon className={`w-8 h-8 mx-auto mb-2 ${dk.textFaint}`} data-id="element-21363" data-mp="3fmoh6dovuurm" />
                            <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-21364" data-mp="248j8569tbupf">
                              Click to upload
                            </p>
                            <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-21365" data-mp="2se86pehvsyyn">
                              16:9 or wider recommended
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </> : pm.productType === 'book' ? <>
                    <CoverImagesSection pm={pm} data-id="element-21366" data-mp="2nlot4hj723tu" />
                    <EditionsSection pm={pm} data-id="element-21367" data-mp="29n8kum5oo59l" />
                    <LinkedEditionsSection data-id="element-21368" data-mp="29qmpy25qeskq" />
                    <ExternalLinksSection pm={pm} data-id="element-21369" data-mp="2enmta5b75h85" />
                  </> : pm.productType !== 'sale' ? <>
                    <MediaUploadSection pm={pm} data-id="element-21370" data-mp="1zs6z80am9lbr" />
                    <PricingSection pm={pm} data-id="element-21371" data-mp="24odvf1d7nigi" />
                    <ExternalLinksSection pm={pm} data-id="element-21372" data-mp="24r9rsl9etz2u" />
                  </> : null}
              </div>
            </div>
          </>}

        {/* Publish Confirmation Modal */}
        {pm.showPublishConfirmModal && <PublishConfirmModal darkMode={pm.darkMode} isProcessing={pm.isProcessing} onConfirm={pm.handleConfirmPublish} onCancel={() => pm.setShowPublishConfirmModal(false)} data-id="element-21373" data-mp="1nfbm54dxct31" />}

        {/* Edition Modal */}
        {pm.showEditionModal && <EditionModal darkMode={pm.darkMode} onAddEdition={pm.addNewEdition} onClose={() => pm.setShowEditionModal(false)} data-id="element-21374" data-mp="1seo3xapxv9yr" />}
      </div>
    </div>;
};
export default ProductManager;